// src/pages/patient/Dashboard.tsx

import { ENV } from '../../config/runtimeEnv';
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import React from "react";
import { getSessionUser, updateSessionUser } from "../../utils/auth/sessionUser";
import { Row, Col, Button, Space, Typography, Progress, Tour, TourProps, theme, Breadcrumb, Tooltip, Avatar, Upload } from "antd";
import type { UploadChangeParam } from "antd/es/upload";
import {
	ExperimentOutlined,
	FileTextOutlined,
	CalendarOutlined,
	EyeOutlined,
	LinkOutlined,
	CalendarFilled,
	DownloadOutlined,
	FormOutlined,
	RightOutlined,
	FolderOutlined,
	FileOutlined,
	HomeOutlined,
	FilePdfOutlined,
	SoundOutlined,
	PlayCircleOutlined,
	UploadOutlined,
	MedicineBoxOutlined,
	UserOutlined,
	CameraOutlined,
} from "@ant-design/icons";

import Layout from "../../components/Layout";
import { PageContainer, SectionCard, StatusTag, InlineLoading, EmptyState } from "../../components/shared";
import AppointmentBookingModal from "./components/AppointmentBookingModal";
import AppointmentManagementModal from "./components/AppointmentManagementModal";
import FormModal from "./components/FormModal";
import GeneticSampleAnalysisModal from "../gene.codes/GeneticWheel/WheelModal";
import OnboardingModal from "./components/OnboardingModal";
import BloodAnalysisModal from "../gene.codes/bloods/BloodAnalysisModal";
import ReportViewer from "./components/ReportViewer";
import PatientBloodTestUploadModal from "./components/PatientBloodTestUploadModal";
import SupplementRegimenModal from "./components/SupplementRegimenModal";
import { NeuralLoader } from "../../components/icons/NeuralLoader";

import { Patient } from "../../types/healthcare/PatientTypes";
import { User } from "../../types/admin/UserTypes";
import { Test } from "../../types/healthcare/TestTypes";
import { AssignedForm } from "../../api/formApi";
import { Appointment } from "../../types/appointments/AppointmentTypes";
import { FileMetadata, ReportFile } from "../../types/file/FileTypes";
import { PatientResourceLink } from "../../types/file/ResourceLinkTypes";
import { SupplementRegimen } from "../../types/healthcare/SupplementTypes";

import { fetchPatientProfile, fetchPatientTests, getPatientAssignedForms, fetchPatientUpcomingAppointments } from "../../api/PatientInterfaceApi";
import { DateTimeUtils } from "../../utils/userPrefs/DateTimeUtils";
import { downloadFile, downloadReport, listPatientReports, listUserFiles } from "../../api/file/fileServiceApi";
import { getPatientResourceLinks } from "../../api/file/patientResourceLinksApi";
import { useNotifications } from "../../context/NotificationContext";
import { useBranding } from "../../context/BrandingContext";
import BrandLogo from "../../components/branding/BrandLogo";
import { updateProfile, uploadProfilePicture } from "../../api/admin/userApi";
import { AudioCacheManager } from "../../services/AudioCacheManager";
import { getPanelsForSample } from "../../services/wheelApiService";
import { normaliseSex } from "../../utils/geneCodes/referenceRange";
import { openGeneCodes } from "../../utils/geneCodes/geneCodesSite";
import { listPatientRegimensPatient } from "../../api/healthcare/SupplementApi";
import { hasFeature } from "../../utils/subscriptions/subscriptionService";

import { SolutionOutlined } from "@ant-design/icons";
import PatientMedicationsSection from "./components/PatientMedicationsSection";
import { fetchPatientMedications } from "../../api/patient/PatientMedicationApi";
import { Medication } from "../../types/healthcare/MedicationTypes";
import { PATIENT_SECTION_BY_KEY, visiblePatientSections, type PatientSectionKey } from "./dashboardSections";
import { useSectionScrollSpy } from "../../hooks/useSectionScrollSpy";
import "./Dashboard.css";

const { Text } = Typography;

const VIEWABLE_RESULT_STATUSES = ["completed", "results_available", "results_reviewed"];

type GeneticAccess = "checking" | "open" | "locked" | "unavailable";

const GENETIC_ACCESS_LABEL: Record<GeneticAccess, string> = { checking: "Checking", open: "View Result", locked: "Locked", unavailable: "Unavailable" };

/** labiq's for-sample rail decides access: wheels mean open, none means locked, a failure means we could not ask. */
const checkGeneticAccess = async (sampleIds: string[], patient: Patient | null): Promise<Record<string, GeneticAccess>> => {
	// No profile means no sex to ask with, and the wheel modal cannot open without one either.
	if (!patient) return Object.fromEntries(sampleIds.map((id): [string, GeneticAccess] => [id, "unavailable"]));
	const outcomes = await Promise.allSettled(sampleIds.map((id) => getPanelsForSample(id, normaliseSex(patient.sex, patient.gender))));
	return Object.fromEntries(outcomes.map((o, i): [string, GeneticAccess] => [sampleIds[i], o.status === "rejected" ? "unavailable" : o.value.length > 0 ? "open" : "locked"]));
};

/** How long a clicked section stays acknowledged (CU-14ygtkj6hp9). */
const NAV_TARGET_ACK_MS = 3000;

/**
 * The count that used to sit on a KPI stat card, now inline in the section's own title.
 *
 * ⚠️ **HIDDEN AT ZERO, never rendered as "0"** — the same rule the sidebar badge follows
 * (decision D3). A row of zeroes is noise, and the two surfaces must agree or the badge and the
 * heading disagree about whether a section has anything in it.
 */
const SectionCount: React.FC<{ value: number }> = ({ value }) =>
	value > 0 ? (
		<Text type="secondary" style={{ fontWeight: 400, fontVariantNumeric: "tabular-nums" }}>
			{value}
		</Text>
	) : null;

const PatientDashboard: React.FC = () => {
	const { showError, showSuccess, showInfo } = useNotifications();
	// S8 (86d3w1g8u) — read only to decide whether the welcome header carries a brand mark
	// at all. See the gate in the header itself for why it is conditional.
	const { branding } = useBranding();
	const [user, setUser] = useState<User>(getSessionUser());
	const isAppointmentAvailable = ENV.VITE_APPOINTMENTS === "true";
	const isPeptideOnly = hasFeature('healthcare.peptide') && !hasFeature('healthcare');

	const { token } = theme.useToken();

	// Tour ref.
	//
	// ⚠️ **THE EIGHT SECTION REFS ARE GONE, AND THEIR REMOVAL IS THE POINT OF THIS SUBTASK.**
	// `testResultsRef`, `formsRef` and friends used to be bound to BOTH the KPI stat tile and the
	// section wrapper. React's later commit won, which was the only reason a stat-card click
	// scrolled to the section rather than to the card itself — a coincidence of render order, not
	// a design. With the tiles deleted the sections address themselves by DOM id from the
	// registry, so the Tour and the scroll resolve the same element by name and the ambiguity
	// cannot come back. `welcomeRef` stays: the Tour's first step targets it and nothing else
	// does.
	const welcomeRef = useRef<HTMLDivElement>(null);

	// Welcome animation
	const [showWelcome, setShowWelcome] = useState(true);
	const [welcomePhase, setWelcomePhase] = useState(0);

	// Modal states
	const [showBookingModal, setShowBookingModal] = useState(false);
	const [showManagementModal, setShowManagementModal] = useState(false);
	const [showTestResultModal, setShowTestResultModal] = useState(false);
	const [showBloodTestResultModal, setShowBloodTestResultModal] = useState(false);
	const [showBloodTestUploadModal, setShowBloodTestUploadModal] = useState(false);
	const [showFormModal, setShowFormModal] = useState(false);
	const [currentOrder, setCurrentOrder] = useState<Test | null>(null);
	const [currentFormId, setCurrentFormId] = useState<number | null>(null);
	// The assignment's access token, carried from the listing into the modal — the modal has
	// no URL for the `?t=` the standalone page reads.
	const [currentFormToken, setCurrentFormToken] = useState<string | undefined>(undefined);

	// Supplement modal
	const [showSupplementModal, setShowSupplementModal] = useState(false);
	const [selectedRegimen, setSelectedRegimen] = useState<SupplementRegimen | null>(null);

	// Medications
	const [medications, setMedications] = useState<Medication[]>([]);

	// Onboarding
	const [showOnboarding, setShowOnboarding] = useState(false);
	const [showTour, setShowTour] = useState(false);

	// File hierarchy
	const [files, setFiles] = useState<FileMetadata[]>([]);
	const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
	const [fileBreadcrumbs, setFileBreadcrumbs] = useState<Array<{ id: string | null; name: string }>>([{ id: null, name: "Home" }]);

	const [reports, setReports] = useState<ReportFile[]>([]);
	const [resourceLinks, setResourceLinks] = useState<PatientResourceLink[]>([]);
	const [supplementRegimens, setSupplementRegimens] = useState<SupplementRegimen[]>([]);
	const [downloadProgress, setDownloadProgress] = useState<{
		[fileId: string]: { progress: number; downloading: boolean; fileName?: string };
	}>({});
	const [selectedReport, setSelectedReport] = useState<ReportFile | null>(null);
	const [showReportViewer, setShowReportViewer] = useState(false);

	// Loading
	const [loading, setLoading] = useState({
		patient: true,
		forms: true,
		tests: true,
		medicalHistory: true,
		appointments: true,
		files: true,
		report: true,
		resourceLinks: true,
		supplements: true,
		medications: true,
	});

	// Data
	const [patientData, setPatientData] = useState<Patient | null>(null);
	const [assignedForms, setAssignedForms] = useState<AssignedForm[]>([]);
	const [testResults, setTestResults] = useState<Test[]>([]);
	const [upcomingAppointments, setUpcomingAppointments] = useState<Appointment[]>([]);
	const [geneticAccess, setGeneticAccess] = useState<Record<string, GeneticAccess>>({});

	const [expandedSections, setExpandedSections] = useState({
		testResults: false,
		forms: false,
		reports: false,
		files: false,
		resourceLinks: false,
		supplements: false,
	});

	const toggleSection = (section: keyof typeof expandedSections) => setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));

	const isProfileComplete = (patient: Patient | null) => !!(patient?.first_name && patient?.last_name && patient?.email && patient?.date_of_birth);

	useEffect(() => {
		loadPatientDashboard();
	}, []);
	useEffect(() => {
		setTimeout(() => setWelcomePhase(1), 600);
		setTimeout(() => setWelcomePhase(2), 1400);
		setTimeout(() => setWelcomePhase(3), 2200);
		setTimeout(() => setShowWelcome(false), 2500);
	}, []);
	useEffect(() => {
		fetchFiles();
	}, [currentFolderId]);

	const loadPatientDashboard = async () => {
		setLoading({
			patient: true,
			forms: true,
			tests: true,
			medicalHistory: true,
			appointments: true,
			files: true,
			report: true,
			resourceLinks: true,
			supplements: true,
			medications: true,
		});
		await Promise.all([fetchPatientData(), fetchFormsData(), fetchTestsData(), fetchAppointmentsData(), fetchFiles(), fetchResourceLinks(), fetchSupplementRegimens(), fetchMedicationsData()]);
	};

	const fetchPatientData = async () => {
		try {
			const response = await fetchPatientProfile(user.id);
			setPatientData(response.user);
			if (!user.xoe_clinical_onboarding && !isProfileComplete(response.user)) {
				setShowOnboarding(true);
			}
		} catch (e) {
			console.error("Error loading patient data:", e);
		} finally {
			setLoading((prev) => ({ ...prev, patient: false }));
		}
	};

	const fetchSupplementRegimens = async () => {
		try {
			const data = await listPatientRegimensPatient(user.id);
			setSupplementRegimens(data.filter((r) => r.status === "active"));
		} catch (e) {
			console.error("Failed to load supplement regimens:", e);
		} finally {
			setLoading((prev) => ({ ...prev, supplements: false }));
		}
	};

	const fetchMedicationsData = async () => {
		try {
			const data = await fetchPatientMedications(user.id);
			setMedications(data);
		} catch (e) {
			console.error("Failed to load medications:", e);
		} finally {
			setLoading((prev) => ({ ...prev, medications: false }));
		}
	};

	useEffect(() => {
		const loadReports = async (sampleId: string) => {
			try {
				const data = await listPatientReports(user.id, sampleId);
				const alreadyLoaded = reports.filter((r) => r.file_name === data[0]?.file_name);
				if (alreadyLoaded.length > 0) return;
				setReports((prev) => [...prev, ...data.filter((r) => !r.clinical_only)]);
			} catch (e) {
				console.error("Failed to load reports:", e);
			} finally {
				setLoading((prev) => ({ ...prev, report: false }));
			}
		};
		for (const test of testResults) {
			if (test.sample_id) loadReports(test.sample_id);
		}
	}, [loading.tests]);

	// Keyed on testResults so a refetch (e.g. after a blood-test upload) re-asks labiq.
	useEffect(() => {
		if (loading.patient) return;
		const sampleIds = Array.from(new Set(testResults.filter((t) => t.test_category === "genetic_testing" && t.sample_id && VIEWABLE_RESULT_STATUSES.includes(t.status)).map((t) => t.sample_id as string)));
		if (sampleIds.length === 0) return;
		let stale = false;
		setGeneticAccess(Object.fromEntries(sampleIds.map((id): [string, GeneticAccess] => [id, "checking"])));
		checkGeneticAccess(sampleIds, patientData).then((access) => {
			if (!stale) setGeneticAccess(access);
		});
		return () => {
			stale = true;
		};
	}, [testResults, patientData, loading.patient]);

	const fetchFiles = async () => {
		try {
			const response = await listUserFiles(user.id, currentFolderId || undefined);
			setFiles(response.filter((f: FileMetadata) => !f.clinical_only));
		} catch (e) {
			console.error("Error loading file data:", e);
		} finally {
			setLoading((prev) => ({ ...prev, files: false }));
		}
	};

	const fetchResourceLinks = async () => {
		try {
			const response = await getPatientResourceLinks(user.id);
			setResourceLinks(response.filter((l: PatientResourceLink) => l.is_active));
		} catch (e) {
			console.error("Error loading resource links:", e);
		} finally {
			setLoading((prev) => ({ ...prev, resourceLinks: false }));
		}
	};

	const fetchFormsData = async () => {
		try {
			setAssignedForms(await getPatientAssignedForms(user.id));
		} catch (e) {
			console.error("Error loading forms:", e);
		} finally {
			setLoading((prev) => ({ ...prev, forms: false }));
		}
	};

	const fetchTestsData = async () => {
		try {
			setTestResults(await fetchPatientTests(user.id));
		} catch (e) {
			console.error("Error loading tests:", e);
		} finally {
			setLoading((prev) => ({ ...prev, tests: false }));
		}
	};

	const fetchAppointmentsData = async () => {
		try {
			setUpcomingAppointments((await fetchPatientUpcomingAppointments(user.id)).data);
		} catch (e) {
			console.error("Error loading appointments:", e);
		} finally {
			setLoading((prev) => ({ ...prev, appointments: false }));
		}
	};

	const handleFolderClick = (folder: FileMetadata) => {
		setCurrentFolderId(folder.id);
		setFileBreadcrumbs([...fileBreadcrumbs, { id: folder.id, name: folder.folder_name || folder.file_name }]);
	};
	const handleBreadcrumbClick = (index: number) => {
		const nb = fileBreadcrumbs.slice(0, index + 1);
		setFileBreadcrumbs(nb);
		setCurrentFolderId(nb[nb.length - 1].id);
	};

	const handleDownloadFile = async (fileId: string, fileName?: string) => {
		try {
			setDownloadProgress((prev) => ({ ...prev, [fileId]: { progress: 0, downloading: true, fileName: fileName || "file" } }));
			const result = await downloadFile(user.id, fileId, (evt: any) => {
				if (evt.lengthComputable) {
					setDownloadProgress((prev) => ({
						...prev,
						[fileId]: { progress: Math.round((evt.loaded * 100) / evt.total), downloading: true, fileName: fileName || "file" },
					}));
				}
			});
			showSuccess(`${result.fileName} downloaded successfully`);
		} catch (e) {
			showError("Failed to download file");
		} finally {
			setTimeout(
				() =>
					setDownloadProgress((prev) => {
						const n = { ...prev };
						delete n[fileId];
						return n;
					}),
				2000,
			);
		}
	};

	const handleReportDownload = async (report: ReportFile) => {
		try {
			setDownloadProgress((prev) => ({ ...prev, [report.id]: { progress: 0, downloading: true, fileName: report.file_name } }));
			const isAudio = report.file_name.toLowerCase().includes("audio");
			if (isAudio) {
				const cache = AudioCacheManager.getInstance();
				await cache.initialize();
				const entry = await cache.get(report.id);
				if (entry) {
					const url = window.URL.createObjectURL(entry.blob);
					const a = document.createElement("a");
					a.href = url;
					a.download = report.file_name.replace(/\.[^.]+$/, ".mp3");
					document.body.appendChild(a);
					a.click();
					document.body.removeChild(a);
					window.URL.revokeObjectURL(url);
					showSuccess(`${report.file_name} downloaded from cache`);
				} else {
					showInfo("Play the audio first to cache it, then download.");
				}
				return;
			}
			await downloadReport(user.id, report.id, (evt: any) => {
				if (evt.lengthComputable) {
					setDownloadProgress((prev) => ({
						...prev,
						[report.id]: { progress: Math.round((evt.loaded * 100) / evt.total), downloading: true, fileName: report.file_name },
					}));
				}
			});
			showSuccess(`${report.file_name} downloaded successfully`);
		} catch (e) {
			showError(`Failed to download ${report.file_name}`);
		} finally {
			setTimeout(
				() =>
					setDownloadProgress((prev) => {
						const n = { ...prev };
						delete n[report.id];
						return n;
					}),
				2000,
			);
		}
	};

	const handleViewReport = (report: ReportFile) => {
		setSelectedReport(report);
		setShowReportViewer(true);
	};

	const handleOpenGeneCodes = () => {
		showInfo("Sign in on gene.codes with your email and password to see this result.");
		openGeneCodes();
	};

	const formatFileSize = (bytes: number) => {
		const sizes = ["Bytes", "KB", "MB", "GB"];
		if (bytes === 0) return "0 Bytes";
		const i = Math.floor(Math.log(bytes) / Math.log(1024));
		return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
	};

	const handleOnboardingComplete = () => {
		setShowOnboarding(false);
		const updated = { ...user, xoe_clinical_onboarding: true };
		if (ENV.VITE_NODE_ENV === "production") {
			updateProfile(updated).then(() => showSuccess("Onboarding completed!"));
		} else {
			updateSessionUser(updated);
			showSuccess("Onboarding completed!");
		}
		setTimeout(() => setShowTour(true), 500);
	};

	const getFolders = () => files.filter((f) => f.is_folder);
	const getFiles = () => files.filter((f) => !f.is_folder);
	const getTotalFileCount = () => getFiles().length;

	// Accent colours from Ant Design design tokens — never hardcoded hex
	const accentColors = {
		tests: token.colorPrimary,
		forms: token.colorSuccess,
		reports: token.purple,
		files: token.colorWarning,
		links: token.cyan,
		supplements: token.magenta,
		appointments: token.colorError,
	};

	// ── Section navigation (S4) ──────────────────────────────────────────────
	//
	// ⚠️ **ONE CALL DECIDES VISIBILITY FOR BOTH THE PAGE AND THE SIDEBAR.** The inline
	// `!isPeptideOnly` / `isAppointmentAvailable` guards that used to sit on each section `Col`
	// are gone; both surfaces read `visibleKeys`, so a section can never appear in the nav and
	// not on the page (or the reverse).
	//
	// Memoised because `<Sidebar>` receives these by reference and this component holds ~20
	// pieces of state — an unmemoised object would re-render the whole sidebar on every keystroke
	// and every fetch settling.
	const visibleSections = useMemo(
		() => visiblePatientSections({ isPeptideOnly, isAppointmentAvailable }),
		[isPeptideOnly, isAppointmentAvailable],
	);
	const visibleKeys = useMemo(() => new Set(visibleSections.map((s) => s.key)), [visibleSections]);

	const sectionCounts = useMemo(
		() => ({
			supplements: supplementRegimens.length,
			forms: assignedForms.length,
			reports: reports.length,
			links: resourceLinks.length,
			resources: getTotalFileCount(),
			results: testResults.length,
			medications: medications.length,
			appointments: upcomingAppointments.length,
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[supplementRegimens, assignedForms, reports, resourceLinks, files, testResults, medications, upcomingAppointments],
	);

	// The nav entries are in-page anchors, not routes — so this scrolls rather than navigates,
	// and every section stays rendered (decision D1). The id comes from the registry, never from
	// a hand-written `patient-section-${key}` template.
	const scrollToSectionKey = (key: PatientSectionKey) =>
		document.getElementById(PATIENT_SECTION_BY_KEY[key].domId)?.scrollIntoView({ behavior: "smooth", block: "start" });

	// ── Scroll-spy (S6) ──────────────────────────────────────────────────────
	//
	// Light the nav entry for whichever section is in view. Not polish: after S3 the patient's
	// default is a COLLAPSED 64px rail with no labels, so the highlight is the only positional
	// feedback there is — without it the icons are shortcuts rather than navigation (D8).
	//
	// Memoised so the observer is not torn down and rebuilt on every one of this component's
	// ~20 state changes. The hook maps the id back through `visibleSections` rather than
	// re-deriving the key from the string.
	const sectionDomIds = useMemo(() => visibleSections.map((s) => s.domId), [visibleSections]);
	const observedSectionDomIds = showWelcome ? [] : sectionDomIds;
	const activeDomId = useSectionScrollSpy(observedSectionDomIds);
	const activeSectionKey = useMemo(
		() => visibleSections.find((s) => s.domId === activeDomId)?.key,
		[visibleSections, activeDomId],
	);

	// CU-14ygtkj6hp9: two sections can share a grid row, so a nav click may scroll nowhere. The
	// clicked target is acknowledged for ~3s and overrides the scroll-spy key; `seq` changes on
	// every click so a repeat of the SAME section re-runs the pulse without remounting the card.
	const [navTarget, setNavTarget] = useState<{ key: PatientSectionKey; seq: number } | null>(null);

	useEffect(() => {
		if (!navTarget) return;
		const timer = setTimeout(() => setNavTarget(null), NAV_TARGET_ACK_MS);
		return () => clearTimeout(timer);
	}, [navTarget]);

	const selectSection = useCallback((key: PatientSectionKey) => {
		setNavTarget((prev) => ({ key, seq: (prev?.seq ?? 0) + 1 }));
		scrollToSectionKey(key);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const effectiveActiveSectionKey = navTarget?.key ?? activeSectionKey;

	const sectionClassName = (key: PatientSectionKey, entry: string) =>
		navTarget?.key === key ? `${entry} mg-patient-section-target mg-patient-section-target-pulse-${navTarget.seq % 2 ? "a" : "b"}` : entry;

	const patientNav = useMemo(
		() => ({
			sections: visibleSections,
			counts: sectionCounts,
			onSectionSelect: selectSection,
			activeSectionKey: effectiveActiveSectionKey,
			onBookAppointment: visibleKeys.has("appointments") ? () => setShowBookingModal(true) : undefined,
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[visibleSections, sectionCounts, visibleKeys, effectiveActiveSectionKey, selectSection],
	);

	/**
	 * Tour copy, keyed by section.
	 *
	 * ⚠️ **The steps are BUILT FROM `visibleSections`, not hand-listed.** The old array carried
	 * its own copy of the peptide/appointment gates and a "Quick Stats" step pointing at the
	 * element this subtask deletes — a null Tour target. Deriving the steps means the Tour can
	 * never again describe a section the page is not rendering.
	 *
	 * Medications gains a step it never had: the old array simply skipped it.
	 */
	const tourCopy: Record<PatientSectionKey, string> = {
		supplements: "View your personalised supplement schedule.",
		forms: "Complete forms assigned by your provider.",
		reports: "Download your generated reports.",
		links: "Access resource links shared by your provider.",
		resources: "Access files shared by your team.",
		results: "View and access all your test results.",
		medications: "Track your prescribed medications and upload scripts.",
		appointments: "Book and manage appointments.",
	};

	const tourSteps: TourProps["steps"] = [
		{ title: "Welcome", description: "Your personal health hub.", target: () => welcomeRef.current },
		...visibleSections.map((section) => ({
			title: section.label,
			description: tourCopy[section.key],
			target: () => document.getElementById(section.domId),
		})),
	];

	if (showWelcome) {
		return (
			// ⚠️ **BOTH <Layout> CALL SITES TAKE `patientNav` — this is the easy one to miss.**
			// This branch is the ~2.5s NeuralLoader splash. Without the prop here the sidebar
			// renders empty for the length of the animation and the section rail pops in
			// afterwards, which reads as a loading bug.
			<Layout isPatient={true} showTour={() => setShowTour(true)} patientNav={patientNav}>
				<div className="min-h-screen relative">
					<NeuralLoader size="full" text="" fullscreen={true} backgroundColor="black" networkColor="white" />
					<div className="absolute inset-0 flex items-center justify-center z-[9999]">
						<div className="text-center max-w-2xl mx-auto px-6">
							{welcomePhase >= 1 && (
								<div className="animate-slide-up-delayed">
									<h1 className="text-6xl font-bold text-white drop-shadow-lg mb-4">Welcome back, {user.first_name}!</h1>
								</div>
							)}
							{welcomePhase >= 2 && (
								<div className="animate-slide-up-super-delayed">
									<p className="text-xl text-white/90 mb-4 leading-relaxed drop-shadow">Your personal health hub is loading</p>
								</div>
							)}
							{welcomePhase >= 3 && (
								<div className="animate-fade-in-final mt-8">
									<Progress percent={100} strokeColor="#ffffff" showInfo={false} className="max-w-xs mx-auto" />
								</div>
							)}
						</div>
					</div>
				</div>
				<style>{`
@keyframes slide-up { from { opacity:0; transform:translateY(30px); } to { opacity:1; transform:translateY(0); } }
@keyframes fade-in { from { opacity:0; } to { opacity:1; } }
.animate-slide-up-delayed { animation: slide-up 0.8s ease-out 0.5s forwards; opacity:0; }
.animate-slide-up-super-delayed { animation: slide-up 0.8s ease-out 1.2s forwards; opacity:0; }
.animate-fade-in-final { animation: fade-in 1s ease-out 2s forwards; opacity:0; }
`}</style>
			</Layout>
		);
	}

	return (
		<Layout isPatient={true} showTour={() => setShowTour(true)} patientNav={patientNav}>
			<OnboardingModal visible={showOnboarding} user={user} setUser={setUser} onComplete={handleOnboardingComplete} />
			<Tour open={showTour} onClose={() => setShowTour(false)} steps={tourSteps} />
			<AppointmentBookingModal visible={showBookingModal} onHide={() => setShowBookingModal(false)} onSuccess={fetchAppointmentsData} />
			<AppointmentManagementModal visible={showManagementModal} onHide={() => setShowManagementModal(false)} onAppointmentChanged={fetchAppointmentsData} />
			{currentFormId && (
				<FormModal
					visible={showFormModal}
					onClose={() => {
						setShowFormModal(false);
						setCurrentFormId(null);
						setCurrentFormToken(undefined);
						fetchFormsData();
					}}
					formId={currentFormId}
					accessToken={currentFormToken}
					title="Complete Form"
				/>
			)}

			<SupplementRegimenModal
				regimen={selectedRegimen}
				visible={showSupplementModal}
				onClose={() => {
					setShowSupplementModal(false);
					setSelectedRegimen(null);
				}}
			/>

			<PageContainer data-testid="patient-portal-root">
				{/* Welcome Header (D6) — one dense row: avatar + greeting, nothing else.
				    NOT a SectionCard: this is a page header, not a content section, and the design
				    system reserves SectionCard for content sections. The old 26px gradient heading
				    also broke the "gradients are for the brand mark tile and the login splash only"
				    rule, and it was the largest thing on a page whose whole purpose is density.
				    Avatar is 32px to match the sidebar's user avatar (Sidebar.tsx `size={32}`) so
				    the two read as the same person at the same weight.
				    ⚠️ LANDMINE — the profile-picture upload lives on this avatar and NOWHERE ELSE
				    in the patient portal. Do not "tidy" the <Upload> away; deleting it silently
				    removes a patient's only way to set their photo.
				    ⚠️ `welcomeRef` is the Tour's first target (tourSteps above) and the avatar must
				    stay a DESCENDANT of `patient-portal-welcome` — the e2e page object locates it as
				    getByTestId('patient-portal-welcome').locator('.ant-avatar'). */}
				<div ref={welcomeRef} data-testid="patient-portal-welcome" className="animate-slide-up" style={{ display: "flex", alignItems: "center", gap: "var(--mg-sp-4)", marginBottom: 16 }}>
					{/* Avatar with profile picture upload on hover */}
					<Tooltip title="Change profile picture">
						<Upload
							accept="image/*"
							showUploadList={false}
							beforeUpload={() => false}
							onChange={async (info: UploadChangeParam) => {
								const file = info.file as unknown as File;
								if (!file) return;
								try {
									const res = await uploadProfilePicture(file);
									const updatedUser = { ...user, profile_picture: res.profile_picture };
									setUser(updatedUser);
									updateSessionUser({ profile_picture: res.profile_picture });
									showSuccess("Profile picture updated");
								} catch {
									showError("Failed to upload profile picture");
								}
							}}
						>
							<div style={{ position: "relative", cursor: "pointer", display: "inline-block" }}>
								<Avatar
									size={32}
									src={user.profile_picture || undefined}
									icon={!user.profile_picture ? <UserOutlined /> : undefined}
									style={{ background: "var(--mg-primary)", flexShrink: 0 }}
								/>
								{/* The scrim covers the whole avatar, so hovering the avatar hovers this,
								    and the click still falls through to the <Upload> trigger above.
								    The icon scales with the avatar: 18/52 ≈ 0.35, and 12px on 32px is
								    the nearest real token (--mg-fs-md) — slightly generous on purpose so
								    it stays legible at this size. The rgba scrim and #fff icon are
								    pre-existing overlay colours, not theme colours; do not add more. */}
								<div style={{
									position: "absolute", inset: 0, borderRadius: "50%",
									background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center",
									justifyContent: "center", opacity: 0, transition: "opacity 200ms",
								}}
									onMouseEnter={e => (e.currentTarget.style.opacity = "1")}
									onMouseLeave={e => (e.currentTarget.style.opacity = "0")}
								>
									<CameraOutlined style={{ color: "#fff", fontSize: "var(--mg-fs-md)" }} />
								</div>
							</div>
						</Upload>
					</Tooltip>
					{/* Greeting — sentence case, no exclamation mark: the full-screen splash above
					    already shouts the same line at 6xl a moment earlier. */}
					<h1 style={{ fontSize: "var(--mg-fs-lg)", fontWeight: "var(--mg-fw-semibold)", lineHeight: "var(--mg-lh-tight)", color: "var(--mg-text)", margin: 0 }}>
						Welcome back, {user.first_name}
					</h1>
					{/* S8 (86d3w1g8u) — the clinic's mark at the row's right edge.
					    ⚠️ This is an ADDITION, not a swap: there is no brand mark here today, so it
					    is gated on `branding.whitelabel` and renders NOTHING for every clinic that
					    is not whitelabelled. Dropping the gate would put a Xoe Med lockup on the
					    patient landing page of every existing tenant. `marginLeft: auto` keeps it
					    off the avatar+greeting pair, which stay exactly where they are. */}
					{branding.whitelabel && (
						<span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", minWidth: 0 }}>
							<BrandLogo height={24} />
						</span>
					)}
				</div>

				{/* Main Content Grid */}
				<Row gutter={[16, 16]}>
					{/* Supplement Regimens */}
					{visibleKeys.has("supplements") && <Col xs={24} lg={12}>
						<div id={PATIENT_SECTION_BY_KEY.supplements.domId} className={sectionClassName("supplements", "animate-slide-in-right")} style={{ animationDelay: "0.6s", height: "100%" }}>
							<SectionCard
								style={{ height: "100%" }}
								title={
									<Space size={8}>
										<MedicineBoxOutlined style={{ fontSize: 18, color: accentColors.supplements }} />
										My Supplements
										<SectionCount value={supplementRegimens.length} />
									</Space>
								}
							>
								{loading.supplements ? (
									<InlineLoading minHeight={200} />
								) : supplementRegimens.length > 0 ? (
									<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
										{(expandedSections.supplements ? supplementRegimens : supplementRegimens.slice(0, 3)).map((regimen, i) => (
											<div
												key={regimen.id}
												className="animate-card-appear"
												style={{ animationDelay: `${i * 0.1}s`, border: "1px solid var(--mg-border)", borderRadius: 8, padding: "12px 16px", cursor: "pointer" }}
												onClick={() => {
													setSelectedRegimen(regimen);
													setShowSupplementModal(true);
												}}
											>
												<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
													<div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
														<Text strong style={{ fontSize: 14, display: "block", marginBottom: 4 }}>
															{regimen.name}
														</Text>
														{regimen.summary && (
															<Text type="secondary" style={{ fontSize: 12, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" } as any}>
																{regimen.summary}
															</Text>
														)}
													</div>
													<Button type="primary" size="large" icon={<EyeOutlined />}>
														View
													</Button>
												</div>
											</div>
										))}
										{supplementRegimens.length > 3 && (
											<Button type="link" size="large" style={{ width: "100%" }} onClick={() => toggleSection("supplements")}>
												{expandedSections.supplements ? "Show less" : `View all ${supplementRegimens.length} regimens`}
												<RightOutlined style={{ fontSize: 12, transform: expandedSections.supplements ? "rotate(90deg)" : "none" }} />
											</Button>
										)}
									</div>
								) : (
									<EmptyState title="No supplement regimens assigned yet" icon={<MedicineBoxOutlined style={{ fontSize: 48, color: "var(--mg-border)" }} />} />
								)}
							</SectionCard>
						</div>
					</Col>}

					{/* Forms */}
					<Col xs={24} lg={12}>
						<div id={PATIENT_SECTION_BY_KEY.forms.domId} className={sectionClassName("forms", "animate-slide-in-left")} style={{ animationDelay: "0.3s", height: "100%" }}>
							<SectionCard
								style={{ height: "100%" }}
								title={
									<Space size={8}>
										<FormOutlined style={{ fontSize: 18, color: accentColors.forms }} />
										Assigned Forms
										<SectionCount value={assignedForms.length} />
									</Space>
								}
								extra={
									!loading.forms && assignedForms.length > 3 ? (
										<Button
											size="small"
											type="link"
											onClick={() => setExpandedSections((prev: any) => ({ ...prev, forms: !prev.forms }))}
										>
											{expandedSections.forms ? "Show less" : "View all"}
										</Button>
									) : undefined
								}
							>
								{loading.forms ? (
									<InlineLoading minHeight={200} />
								) : assignedForms.length > 0 ? (
									<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
										{(expandedSections.forms ? assignedForms : assignedForms.slice(0, 3)).map((form, i) => (
											<div
												key={form.id}
												className="animate-card-appear"
												style={{ animationDelay: `${i * 0.1}s`, border: "1px solid var(--mg-border)", borderRadius: 8, padding: "12px 16px", cursor: "pointer" }}
												onClick={() => {
													setCurrentFormId(form.id);
													setCurrentFormToken(form.access_token);
													setShowFormModal(true);
												}}
											>
												<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
													<div style={{ flex: 1 }}>
														<Text strong style={{ fontSize: 14, display: "block", marginBottom: 4 }}>
															{form.form?.title || "Unknown Form"}
														</Text>
														{form.notes && (
															<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
																{form.notes}
															</Text>
														)}
														<Text type="secondary" style={{ fontSize: 12 }}>
															{DateTimeUtils.formatLocalDate(form.created_at, { year: "numeric", month: "short", day: "numeric" })}
														</Text>
													</div>
													{/* F6: this hand-rolled ternary knew only Completed and In Progress, so
													    "Reviewed" and "Cancelled" both fell through to the pending colour —
													    a cancelled form looked identical to one still awaiting the patient.
													    `STATUS_MAP` covers all five canonical values, so the mapping belongs
													    there and not here. `label` keeps the server's exact casing. */}
													<StatusTag status={form.status} label={form.status} />
												</div>
											</div>
										))}
										{assignedForms.length > 3 && (
											<Button type="link" size="large" style={{ width: "100%" }} onClick={() => toggleSection("forms")}>
												{expandedSections.forms ? "Show less" : `View all ${assignedForms.length} forms`}
												<RightOutlined style={{ fontSize: 12, transform: expandedSections.forms ? "rotate(90deg)" : "none" }} />
											</Button>
										)}
									</div>
								) : (
									<EmptyState title="No forms assigned" icon={<FileTextOutlined style={{ fontSize: 48, color: "var(--mg-border)" }} />} />
								)}
							</SectionCard>
						</div>
					</Col>

					{/* Reports */}
					{visibleKeys.has("reports") && <Col xs={24} lg={12}>
						<div id={PATIENT_SECTION_BY_KEY.reports.domId} data-testid="patient-portal-reports-section" className={sectionClassName("reports", "animate-slide-in-right")} style={{ animationDelay: "0.4s", height: "100%" }}>
							<SectionCard
								style={{ height: "100%" }}
								title={
									<Space size={8}>
										<FileTextOutlined style={{ fontSize: 18, color: accentColors.reports }} />
										Reports
										<SectionCount value={reports.length} />
									</Space>
								}
								extra={
									!loading.report && reports.length > 3 ? (
										<Button
											size="small"
											type="link"
											onClick={() => setExpandedSections((prev: any) => ({ ...prev, reports: !prev.reports }))}
										>
											{expandedSections.reports ? "Show less" : "View all"}
										</Button>
									) : undefined
								}
							>
								{loading.report ? (
									<InlineLoading minHeight={200} />
								) : reports.length > 0 ? (
									<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
										{(expandedSections.reports ? reports : reports.slice(0, 3)).map((report, i) => {
											const dl = downloadProgress[report.id];
											const isAudio = report.file_name.toLowerCase().includes("audio");
											return (
												<div key={report.id} className="animate-card-appear" style={{ animationDelay: `${i * 0.1}s`, border: "1px solid var(--mg-border)", borderRadius: 8, padding: "12px 16px" }}>
													<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
														<div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
															{isAudio ? <SoundOutlined style={{ fontSize: 28, color: accentColors.reports }} /> : <FilePdfOutlined style={{ fontSize: 28, color: "var(--mg-danger)" }} />}
															<div style={{ flex: 1, minWidth: 0 }}>
																<Text strong style={{ fontSize: 14, display: "block", marginBottom: 4 }} ellipsis>
																	{report.file_name}
																</Text>
																<Text type="secondary" style={{ fontSize: 12 }}>
																	{new Date(report.created_at).toLocaleDateString()}
																</Text>
																{dl?.downloading && <Progress percent={dl.progress} size="small" status="active" style={{ marginTop: 4 }} />}
															</div>
														</div>
														<Space>
															<Tooltip title={isAudio ? "Stream" : "View PDF"}>
																<Button type="primary" size="middle" icon={isAudio ? <PlayCircleOutlined /> : <EyeOutlined />} onClick={() => handleViewReport(report)}>
																	{isAudio ? "Play" : "View"}
																</Button>
															</Tooltip>
															<Button size="middle" icon={<DownloadOutlined />} onClick={() => handleReportDownload(report)} loading={dl?.downloading} />
														</Space>
													</div>
												</div>
											);
										})}
										{reports.length > 3 && (
											<Button type="link" size="large" style={{ width: "100%" }} onClick={() => toggleSection("reports")}>
												{expandedSections.reports ? "Show less" : `View all ${reports.length} reports`}
												<RightOutlined style={{ fontSize: 12, transform: expandedSections.reports ? "rotate(90deg)" : "none" }} />
											</Button>
										)}
									</div>
								) : (
									<EmptyState title="No reports available" icon={<FileTextOutlined style={{ fontSize: 48, color: "var(--mg-border)" }} />} />
								)}
							</SectionCard>
						</div>
					</Col>}

					{/* Helpful Links */}
					<Col xs={24} lg={12}>
						{/* ⚠️ The heading reads from the registry, not a literal. Two strings for this one
						    section used to ship side by side — the deleted KPI tile said "Helpful Links"
						    and this header said "Handy Links". The registry is now the only copy, so the
						    nav entry and the heading cannot disagree again. */}
						<div id={PATIENT_SECTION_BY_KEY.links.domId} className={sectionClassName("links", "animate-slide-in-left")} style={{ animationDelay: "0.7s", height: "100%" }}>
							<SectionCard
								style={{ height: "100%" }}
								title={
									<Space size={8}>
										<LinkOutlined style={{ fontSize: 18, color: accentColors.links }} />
										{PATIENT_SECTION_BY_KEY.links.label}
										<SectionCount value={resourceLinks.length} />
									</Space>
								}
							>
								{loading.resourceLinks ? (
									<InlineLoading minHeight={200} />
								) : resourceLinks.length > 0 ? (
									<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
										{(expandedSections.resourceLinks ? resourceLinks : resourceLinks.slice(0, 3)).map((link, i) => (
											<div
												key={`${link.patient_id}-${link.resource_type}-${i}`}
												className="animate-card-appear"
												style={{ animationDelay: `${i * 0.1}s`, border: "1px solid var(--mg-border)", borderRadius: 8, padding: "12px 16px", cursor: "pointer" }}
												onClick={() => window.open(link.link_url, "_blank", "noopener,noreferrer")}
											>
												<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
													<div style={{ flex: 1, minWidth: 0 }}>
														<Text strong style={{ fontSize: 14, display: "block", marginBottom: 4 }} ellipsis>
															{link.resource_type}
														</Text>
														<Text type="secondary" style={{ fontSize: 12 }}>
															{link.description}
														</Text>
													</div>
													<LinkOutlined style={{ fontSize: 20, marginLeft: 12, color: accentColors.links }} />
												</div>
											</div>
										))}
										{resourceLinks.length > 3 && (
											<Button type="link" size="large" style={{ width: "100%" }} onClick={() => toggleSection("resourceLinks")}>
												{expandedSections.resourceLinks ? "Show less" : `View all ${resourceLinks.length} links`}
												<RightOutlined style={{ fontSize: 12, transform: expandedSections.resourceLinks ? "rotate(90deg)" : "none" }} />
											</Button>
										)}
									</div>
								) : (
									<EmptyState title="No resource links available" icon={<LinkOutlined style={{ fontSize: 48, color: "var(--mg-border)" }} />} />
								)}
							</SectionCard>
						</div>
					</Col>

					{/* Resources / Files */}
					<Col xs={24} lg={12}>
						<div id={PATIENT_SECTION_BY_KEY.resources.domId} className={sectionClassName("resources", "animate-slide-in-left")} style={{ animationDelay: "0.5s", height: "100%" }}>
							<SectionCard
								style={{ height: "100%" }}
								title={
									<Space size={8}>
										<FileOutlined style={{ fontSize: 18, color: accentColors.files }} />
										Resources
										<SectionCount value={getTotalFileCount()} />
									</Space>
								}
							>
								{loading.files ? (
									<InlineLoading minHeight={200} />
								) : (
									<>
										{fileBreadcrumbs.length > 1 && (
											<div style={{ marginBottom: 16, padding: "8px 12px", border: "1px solid var(--mg-border)", borderRadius: 8 }}>
												<Breadcrumb>
													{fileBreadcrumbs.map((crumb, i) => (
														<Breadcrumb.Item key={crumb.id || "root"}>
															<span onClick={() => handleBreadcrumbClick(i)} style={{ cursor: "pointer" }}>
																{i === 0 ? <HomeOutlined /> : crumb.name}
															</span>
														</Breadcrumb.Item>
													))}
												</Breadcrumb>
											</div>
										)}
										{getFolders().length > 0 && (
											<div style={{ marginBottom: 16 }}>
												<Text type="secondary" style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", display: "block", marginBottom: 8 }}>
													Folders
												</Text>
												<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
													{getFolders()
														.slice(0, expandedSections.files ? undefined : 3)
														.map((folder, i) => (
															<div
																key={folder.id}
																className="animate-card-appear"
																style={{ animationDelay: `${i * 0.1}s`, border: "1px solid var(--mg-border)", borderRadius: 8, padding: "12px 16px", cursor: "pointer" }}
																onClick={() => handleFolderClick(folder)}
															>
																<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
																	<Space size={12}>
																		<FolderOutlined style={{ fontSize: 28, color: token.colorWarning }} />
																		<div>
																			<Text strong style={{ fontSize: 14, display: "block", marginBottom: 2 }}>
																				{folder.folder_name}
																			</Text>
																			<Text type="secondary" style={{ fontSize: 12 }}>
																				{DateTimeUtils.formatLocalDate(folder.created_at, { year: "numeric", month: "short", day: "numeric" })}
																			</Text>
																		</div>
																	</Space>
																	<RightOutlined style={{ color: "var(--mg-text-secondary)" }} />
																</div>
															</div>
														))}
												</div>
											</div>
										)}
										{getFiles().length > 0 ? (
											<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
												{getFolders().length > 0 && (
													<Text type="secondary" style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", display: "block", marginBottom: 4 }}>
														Files
													</Text>
												)}
												{getFiles()
													.slice(0, expandedSections.files ? undefined : 3)
													.map((file, i) => {
														const dl = downloadProgress[file.id];
														return (
															<div key={file.id} className="animate-card-appear" style={{ animationDelay: `${i * 0.1}s`, border: "1px solid var(--mg-border)", borderRadius: 8, padding: "12px 16px" }}>
																<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
																	<Space size={12} style={{ flex: 1, minWidth: 0 }}>
																		<FileOutlined style={{ fontSize: 24, color: accentColors.reports }} />
																		<div style={{ flex: 1, minWidth: 0 }}>
																			<Text strong style={{ fontSize: 14, display: "block", marginBottom: 2 }} ellipsis>
																				{file.file_name}
																			</Text>
																			<Text type="secondary" style={{ fontSize: 12 }}>
																				{file.file_type} · {formatFileSize(file.file_size)}
																			</Text>
																			{dl?.downloading && <Progress percent={dl.progress > 0 ? dl.progress : undefined} size="small" status="active" showInfo={dl.progress > 0} style={{ marginTop: 4 }} />}
																		</div>
																	</Space>
																	<Button type="primary" size="middle" icon={<DownloadOutlined />} onClick={() => handleDownloadFile(file.id, file.file_name)} loading={dl?.downloading} style={{ marginLeft: 8 }} />
																</div>
															</div>
														);
													})}
											</div>
										) : getFolders().length === 0 ? (
											<EmptyState title="No files available" icon={<FileTextOutlined style={{ fontSize: 48, color: "var(--mg-border)" }} />} />
										) : null}
										{files.length > 3 && (
											<Button type="link" size="large" style={{ width: "100%", marginTop: 8 }} onClick={() => toggleSection("files")}>
												{expandedSections.files ? "Show less" : `View all ${files.length} items`}
												<RightOutlined style={{ fontSize: 12, transform: expandedSections.files ? "rotate(90deg)" : "none" }} />
											</Button>
										)}
									</>
								)}
							</SectionCard>
						</div>
					</Col>

					{/* Test Results */}
					<Col xs={24} lg={12}>
						<div id={PATIENT_SECTION_BY_KEY.results.domId} data-testid="patient-portal-results-section" className={sectionClassName("results", "animate-slide-in-right")} style={{ animationDelay: "0.2s", height: "100%" }}>
							<SectionCard
								style={{ height: "100%" }}
								title={
									<Space size={8}>
										<ExperimentOutlined style={{ fontSize: 18, color: accentColors.tests }} />
										Test Results
										<SectionCount value={testResults.length} />
									</Space>
								}
								extra={
									<Button type="primary" icon={<UploadOutlined />} onClick={() => setShowBloodTestUploadModal(true)} size="small">
										Upload Blood Test
									</Button>
								}
							>
								{loading.tests ? (
									<InlineLoading minHeight={200} />
								) : testResults.length > 0 ? (
									<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
										{(expandedSections.testResults ? testResults : testResults.slice(0, 3)).map((test, i) => {
											const isGenetic = test.test_category === "genetic_testing";
											const isViewable = VIEWABLE_RESULT_STATUSES.includes(test.status);
											const access: GeneticAccess = isGenetic && isViewable ? (test.sample_id ? (geneticAccess[test.sample_id] ?? "checking") : "locked") : "open";
											const handleClick = () => {
												if (!isViewable) {
													showInfo("Results not yet available.");
													return;
												}
												if (access === "checking") return;
												if (access === "locked") {
													showInfo("This result is locked. Please contact your provider.");
													return;
												}
												if (access === "unavailable") {
													showInfo("We couldn't check access to this result. If this keeps happening, please contact your clinic.");
													return;
												}
												if (test.test_category === "blood_tests") {
													setCurrentOrder(test);
													setShowBloodTestResultModal(true);
													return;
												}
												if (ENV.VITE_GENETIC_WHEEL !== "true") {
													handleOpenGeneCodes();
												} else {
													setCurrentOrder(test);
													setShowTestResultModal(true);
												}
											};
											return (
												<div
													key={test.id}
													className="animate-card-appear"
													style={{ animationDelay: `${i * 0.1}s`, border: "1px solid var(--mg-border)", borderRadius: 8, padding: "12px 16px", cursor: "pointer" }}
													onClick={handleClick}
												>
													<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
														<div style={{ flex: 1 }}>
															<Text strong style={{ fontSize: 14, display: "block", marginBottom: 4 }}>
																{test.sample_id}
															</Text>
															<Text type="secondary" style={{ fontSize: 12 }}>
																{DateTimeUtils.formatLocalDate(test.created_at, { year: "numeric", month: "short", day: "numeric" })}
															</Text>
														</div>
														<Button
															type={["completed", "results_available"].includes(test.status) && access !== "unavailable" ? "primary" : "default"}
															size="large"
															icon={<EyeOutlined />}
															loading={access === "checking"}
															disabled={!isViewable || access === "locked"}
															// Unavailable stays clickable so its message can show, but reads like the disabled locked state.
															style={access === "unavailable" ? { color: token.colorTextDisabled, background: token.colorBgContainerDisabled, borderColor: token.colorBorder } : undefined}
														>
															{isViewable ? GENETIC_ACCESS_LABEL[access] : test.status}
														</Button>
													</div>
												</div>
											);
										})}
										{testResults.length > 3 && (
											<Button type="link" size="large" style={{ width: "100%" }} onClick={() => toggleSection("testResults")}>
												{expandedSections.testResults ? "Show less" : `View all ${testResults.length} results`}
												<RightOutlined style={{ fontSize: 12, transform: expandedSections.testResults ? "rotate(90deg)" : "none" }} />
											</Button>
										)}
									</div>
								) : (
									<EmptyState title="No test results available" icon={<ExperimentOutlined style={{ fontSize: 48, color: "var(--mg-border)" }} />} />
								)}
							</SectionCard>
						</div>
					</Col>

					{/* Medications.
					    ⚠️ This section delegates its whole card — including the title — to
					    PatientMedicationsSection, so its inline count lives THERE, not here. Adding
					    one to this wrapper would render two titles. */}
					<Col xs={24} lg={12}>
						<div id={PATIENT_SECTION_BY_KEY.medications.domId} className={sectionClassName("medications", "animate-slide-in-left")} style={{ animationDelay: "0.65s", height: "100%" }}>
							<PatientMedicationsSection medications={medications} loading={loading.medications} patientId={user.id} onRefresh={fetchMedicationsData} />
						</div>
					</Col>

					{/* Appointments */}
					{visibleKeys.has("appointments") && (
						<Col xs={24} lg={12}>
							<div id={PATIENT_SECTION_BY_KEY.appointments.domId} className={sectionClassName("appointments", "animate-slide-in-right")} style={{ animationDelay: "0.8s", height: "100%" }}>
								<SectionCard
									style={{ height: "100%" }}
									title={
										<Space size={8}>
											<CalendarOutlined style={{ fontSize: 18, color: accentColors.appointments }} />
											Upcoming Appointments
											<SectionCount value={upcomingAppointments.length} />
										</Space>
									}
									extra={
										<Space size="small">
											<Button type="primary" size="middle" icon={<CalendarFilled />} onClick={() => setShowBookingModal(true)}>
												Book
											</Button>
											<Button size="middle" icon={<CalendarOutlined />} onClick={() => setShowManagementModal(true)}>
												Manage
											</Button>
										</Space>
									}
								>
									{loading.appointments ? (
										<InlineLoading minHeight={200} />
									) : upcomingAppointments.length > 0 ? (
										<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
											{upcomingAppointments.slice(0, 6).map((apt, i) => (
												<div
													key={apt.id}
													className="animate-card-appear"
													style={{
														animationDelay: `${i * 0.1}s`,
														border: "1px solid var(--mg-border)",
														borderRadius: 8,
														padding: "12px 16px",
														cursor: apt.location === "Online" && apt.consultation_link ? "pointer" : "default",
													}}
													onClick={() => apt.location === "Online" && apt.consultation_link && window.open(apt.consultation_link, "_blank", "noopener,noreferrer")}
												>
													<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
														<div style={{ flex: 1 }}>
															<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
																<Text strong style={{ fontSize: 14 }}>
																	{apt.title}
																</Text>
																{apt.location === "online" && (
																	<span style={{ marginLeft: 8 }}><StatusTag status="active" label="Online" /></span>
																)}
															</div>
															<div style={{ display: "flex", alignItems: "center", gap: 24, fontSize: 13, color: "var(--mg-text-secondary)" }}>
																<span>
																	<CalendarOutlined style={{ marginRight: 4 }} />
																	{DateTimeUtils.formatLocalDate(apt.start_time as Date, { year: "numeric", month: "short", day: "numeric" })}
																</span>
																<span>🕐 {DateTimeUtils.formatLocalTime(apt.start_time as Date)}</span>
																<span>
																	<HomeOutlined style={{ marginRight: 4 }} />
																	{apt.location}
																</span>
															</div>
														</div>
													</div>
												</div>
											))}
										</div>
									) : (
										<EmptyState title="No upcoming appointments" icon={<CalendarOutlined style={{ fontSize: 48, color: "var(--mg-border)" }} />} />
									)}
								</SectionCard>
							</div>
						</Col>
					)}
				</Row>
			</PageContainer>

			{/* Floating Modals */}
			{showTestResultModal && patientData && currentOrder && <GeneticSampleAnalysisModal isOpen={showTestResultModal} onClose={() => setShowTestResultModal(false)} patient={patientData} sample={{ sampleID: currentOrder.sample_id! } as any} surface="patient-dashboard" />}
			{selectedReport && (
				<ReportViewer
					visible={showReportViewer}
					onClose={() => {
						setShowReportViewer(false);
						setSelectedReport(null);
					}}
					report={selectedReport}
					patientId={user.id}
				/>
			)}
			{showBloodTestResultModal && patientData && currentOrder && <BloodAnalysisModal isOpen={showBloodTestResultModal} onClose={() => setShowBloodTestResultModal(false)} patient={patientData} test={currentOrder} alltests={testResults} />}
			<PatientBloodTestUploadModal isOpen={showBloodTestUploadModal} onClose={() => setShowBloodTestUploadModal(false)} onSuccess={fetchTestsData} />

			<style>{`
@keyframes slide-up { from { opacity:0; transform:translateY(30px); } to { opacity:1; transform:translateY(0); } }
@keyframes slide-in-right { from { opacity:0; transform:translateX(30px); } to { opacity:1; transform:translateX(0); } }
@keyframes slide-in-left { from { opacity:0; transform:translateX(-30px); } to { opacity:1; transform:translateX(0); } }
@keyframes fade-in { from { opacity:0; } to { opacity:1; } }
@keyframes card-appear { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
.animate-slide-up { animation: slide-up 0.6s ease-out; }
.animate-slide-in-right { animation: slide-in-right 0.6s cubic-bezier(0.4,0,0.2,1); animation-fill-mode:both; }
.animate-slide-in-left { animation: slide-in-left 0.6s cubic-bezier(0.4,0,0.2,1); animation-fill-mode:both; }
.animate-fade-in-staggered { animation: fade-in 0.6s ease-out; animation-fill-mode:both; opacity:0; }
.animate-card-appear { animation: card-appear 0.4s ease-out; animation-fill-mode:both; opacity:0; }
html { scroll-behavior: smooth; }
/* Sidebar section nav (S4): keep a scrolled-to heading clear of the FIXED top bar.
   Without this the heading lands underneath it and the scroll looks broken.
   ⚠️ Derived from --mg-topbar-h, never hardcoded 44px — if the bar's height changes, an
   anchor with a literal offset silently drifts back under it. The +--mg-sp-6 matches the
   16px gap .mg-layout-content already leaves below the bar.
   ⚠️ NOTE: the ticket specified --mg-space-md; that token DOES NOT EXIST. The real spacing
   scale in styles/global.css is --mg-sp-0 … --mg-sp-10. */
[id^="patient-section-"] { scroll-margin-top: calc(var(--mg-topbar-h) + var(--mg-sp-6)); }
`}</style>
		</Layout>
	);
};

export default PatientDashboard;
